import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import LocationPicker from './LocationPicker';
import { DateTimePicker } from './ui/DateTimePicker';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * ContactModal — shown when a client taps "Contact Provider" from
 * ServiceCatalogPage or ProviderProfilePage.
 *
 * Props:
 *   open           boolean
 *   onClose        () => void
 *   providerId     string
 *   providerName   string
 *   subcategoryId  string | null   — subcategory slug (preferred)
 *   subcategoryName string | null  — display name (resolved by parent)
 */
const ContactModal = ({ open, onClose, providerId, providerName, subcategoryId, subcategoryName }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [scheduledDate, setScheduledDate] = useState('');
  const [message, setMessage] = useState('');
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error(t('contact.message'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        provider_id: providerId,
        subcategory_id: subcategoryId || undefined,
        message: message.trim(),
        preferred_date: scheduledDate || undefined,
        location: location ?? { address: '', lat: null, lng: null },
      };

      const res = await axios.post(`${API}/contracts/direct`, payload);
      toast.success(t('contact.success'));
      onClose();
      navigate(`/service/${res.data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.error === 'client_email_unverified' || detail?.error === 'client_id_required') {
        const msg = detail.message?.[i18n.language] || detail.message?.es;
        toast.error(msg || t('verify.required_to_book'));
        onClose();
        navigate('/verify');
      } else if (typeof detail?.message === 'object') {
        // provider_not_verified / provider_license_required
        toast.error(detail.message?.[i18n.language] || detail.message?.es || 'Request failed');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to send request');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('contact.title')}{providerName ? ` · ${providerName}` : ''}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {subcategoryName && (
            <div className="text-sm text-muted-foreground bg-gray-50 rounded-lg px-3 py-2">
              {subcategoryName}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{t('contact.scheduled_date')}</Label>
            <DateTimePicker
              value={scheduledDate}
              onChange={setScheduledDate}
              placeholder={t('datetime.no_date')}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('contact.address')}</Label>
            <LocationPicker
              onLocationSelect={setLocation}
              placeholder={t('contact.address_placeholder')}
            />
            {location?.address && (
              <p className="text-xs text-muted-foreground truncate">{t('location.selected')}: {location.address}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cm-message">{t('contact.message')} *</Label>
            <Textarea
              id="cm-message"
              placeholder={t('contact.message_placeholder')}
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={4}
              required
            />
          </div>

          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !message.trim()}
              className="flex-1"
              style={{ background: 'hsl(48 100% 50%)', color: 'hsl(215 25% 12%)' }}
            >
              {loading ? t('contact.sending') : t('contact.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;